import React from 'react';
import { PnidEquipment, PnidNode, MarkingStatus, MARKING_STATUS_STYLES } from '../types';

export type WizardStep = 1 | 2 | 3 | 4 | 5;

type DrawTargetTone = 'new' | 'missing' | MarkingStatus;

interface DrawTargetInfo {
  label: string;
  detail?: string;
  tone?: DrawTargetTone;
}

interface ContextBarProps {
  step: WizardStep;
  pnidNo?: string | null;
  // Equipment context (step 3)
  equipments?: PnidEquipment[];
  activeEquipmentId?: string | null;
  onSelectEquipment?: (id: string | null) => void;
  onEditEquipment?: (equipmentId: string) => void;
  // Node context (step 4)
  nodes?: PnidNode[];
  activeNodeId?: string | null;
  onSelectNode?: (id: string | null) => void;
  // Node actions
  onMarkNode?: (nodeId: string) => void;
  onUnmarkNode?: (nodeId: string) => void;
  onEditNode?: (nodeId: string) => void;
  isNodeMarkingMode?: boolean;
  // Navigation
  onNext?: () => void;
  hasNext?: boolean;
  // Counts for context
  equipmentCount?: { total: number; marked: number };
  nodeCount?: { total: number; marked: number; skipped?: number };
  instrumentCount?: { equipment: number; node: number };
  drawTarget?: DrawTargetInfo | null;
  // Steps 3-4: Show active only toggle
  showActiveOnly?: boolean;
  onShowActiveOnlyChange?: (enabled: boolean) => void;
  // Step 2: Template Annotation Navigation
  templateAnnotationCount?: { total: number; verified: number };
  activeAnnotationId?: string | null;
  unverifiedOnlyFilter?: boolean;
  onUnverifiedOnlyFilterChange?: (enabled: boolean) => void;
  onNavigateAnnotation?: (direction: 'prev' | 'next') => void;
}



const ContextBar: React.FC<ContextBarProps> = ({
  step,
  equipments = [],
  activeEquipmentId,
  onEditEquipment,
  nodes = [],
  activeNodeId,
  onMarkNode,
  onUnmarkNode,
  onEditNode: _onEditNode,
  isNodeMarkingMode = false,
  onNext,
  hasNext,
  equipmentCount,
  nodeCount,
  drawTarget,
  // Steps 3-4 props
  showActiveOnly = false,
  onShowActiveOnlyChange,
  // Step 2 props
  templateAnnotationCount,
  activeAnnotationId: _activeAnnotationId,
  unverifiedOnlyFilter,
  onUnverifiedOnlyFilterChange,
  onNavigateAnnotation,
}) => {




  const showEquipmentSelector = step === 3;
  const showNodeSelector = step === 4;

  // Get active item info
  const activeEquipment = equipments.find((e) => e.id === activeEquipmentId);
  const activeNode = nodes.find((n) => n.id === activeNodeId);

  // Sort items: unmarked first, then by name


  const showTarget = Boolean(drawTarget?.label);

  const getTargetStyle = (tone?: DrawTargetTone) => {
    if (!tone || tone === 'new') {
      return { bg: 'rgba(243, 244, 246, 0.9)', border: 'rgba(229, 231, 235, 0.8)', text: '#374151' };
    }
    if (tone === 'missing') {
      return { bg: 'rgba(254, 243, 199, 0.9)', border: 'rgba(253, 230, 138, 0.8)', text: '#92400e' };
    }
    const statusStyle = MARKING_STATUS_STYLES[tone];
    return { bg: statusStyle.fill, border: statusStyle.color, text: statusStyle.color };
  };



  return (
    /* One row, not a stacked card: this sits in the workspace band, where every
       pixel of height is taken from the drawing. The card chrome (blur, shadow,
       radius) went with the stacking — the band already carries the surface. */
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
        minWidth: 0,
      }}
    >
      {/* The step number/name and the P&ID number used to open this row. Both
          are now stated once above — the step as the active header tab, the
          drawing as the page title — and repeating them here wrapped the row
          onto a second line, costing the canvas ~30px. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>

        {/* Equipment Edit Button */}
        {showEquipmentSelector && activeEquipment && onEditEquipment && (
          <button
            onClick={() => onEditEquipment(activeEquipment.id)}
            style={{
              height: 22,
              padding: '0 6px',
                minHeight: 24,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              color: '#374151',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: 3,
            }}
            title="Edit equipment details"
          >
            ✎ Edit
          </button>
        )}

        {/* Node Action Buttons */}
        {showNodeSelector && activeNode && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ 
              fontSize: 12,
              color: 'var(--raven-text-secondary)',
              maxWidth: 100,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {activeNode.source_ref || '?'} → {activeNode.dest_ref || '?'}
            </span>
            
            {isNodeMarkingMode ? (
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  color: '#2563eb',
                }}
              >
                <span style={{ 
                  width: 6, 
                  height: 6, 
                  borderRadius: 99,
                  backgroundColor: '#2563eb',
                  animation: 'pulse 1.5s infinite',
                }} />
                Drawing
              </span>
            ) : activeNode.status === 'ai_identified' ? (
              onMarkNode && (
                <button
                  onClick={() => onMarkNode(activeNode.id)}
                  style={{
                    height: 22,
                    padding: '0 8px',
                minHeight: 24,
                    backgroundColor: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  Mark
                </button>
              )
            ) : (
              <>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    color: 'var(--raven-success)',
                  }}
                >
                  <span style={{ 
                    width: 6, 
                    height: 6, 
                    borderRadius: 99,
                    backgroundColor: '#16a34a',
                  }} />
                  Marked
                </span>
                {onUnmarkNode && (
                  <button
                    onClick={() => onUnmarkNode(activeNode.id)}
                    style={{
                      height: 22,
                      padding: '0 8px',
                minHeight: 24,
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      color: 'var(--raven-text-secondary)',
                      border: '1px solid rgba(0, 0, 0, 0.1)',
                      borderRadius: 4,
                      cursor: 'pointer',
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                    title="Clear the marked path and redraw"
                  >
                    Unmark
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* Step 2: Template Annotation Navigation */}
        {step === 2 && templateAnnotationCount && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 8,
            marginLeft: 8,
          }}>
            {/* Progress indicator */}
            <span style={{ fontSize: 12, color: 'var(--raven-text-secondary)', whiteSpace: 'nowrap' }}>
              <span style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                padding: '2px 6px', 
                background: '#dcfce7', 
                borderRadius: 4,
                marginRight: 4,
              }}>
                <span style={{ color: '#16a34a', fontWeight: 600 }}>
                  {templateAnnotationCount.verified}
                </span>
                <span style={{ color: '#16a34a' }}>✓</span>
              </span>
              <span style={{ color: 'var(--raven-text-secondary)' }}>/</span>
              <span style={{ color: '#374151', fontWeight: 500 }}>{templateAnnotationCount.total}</span>
            </span>

            {/* Unverified only chip */}
            <button
              onClick={() => {
                const newFilterValue = !unverifiedOnlyFilter;
                onUnverifiedOnlyFilterChange?.(newFilterValue);
                // When enabling the filter, navigate to first unverified annotation
                if (newFilterValue && onNavigateAnnotation) {
                  // Use setTimeout to ensure state update propagates first
                  setTimeout(() => onNavigateAnnotation('next'), 0);
                }
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '3px 8px',
                    minHeight: 24,
                fontSize: 12,
                fontWeight: 500,
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
                background: unverifiedOnlyFilter ? '#fef08a' : '#f1f5f9',
                color: unverifiedOnlyFilter ? '#a16207' : '#64748b',
                transition: 'all 0.15s',
              }}
              title="Filter to show only unverified annotations"
            >
              <span style={{ fontSize: 8 }}>{unverifiedOnlyFilter ? '◉' : '○'}</span>
              Unverified only
            </button>

            {/* Navigation arrows */}
            <div style={{ 
              display: 'flex', 
              gap: 2,
            }}>
              <button
                onClick={() => onNavigateAnnotation?.('prev')}
                style={{
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid #e5e7eb',
                  borderRadius: 4,
                  background: 'white',
                  cursor: 'pointer',
                  fontSize: 12,
                  color: '#64748b',
                }}
                title="Previous annotation (Shift+Tab)"
              >
                ←
              </button>
              <button
                onClick={() => onNavigateAnnotation?.('next')}
                style={{
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid #e5e7eb',
                  borderRadius: 4,
                  background: 'white',
                  cursor: 'pointer',
                  fontSize: 12,
                  color: '#64748b',
                }}
                title="Next annotation (Tab)"
              >
                →
              </button>
            </div>
          </div>
        )}

        {/* Active Only toggle (steps 3-4) */}
        {(step === 3 || step === 4) && onShowActiveOnlyChange && (
          <button
            onClick={() => onShowActiveOnlyChange(!showActiveOnly)}
            style={{
              padding: '3px 8px',
                    minHeight: 24,
              fontSize: 12,
              borderRadius: 10,
              border: showActiveOnly ? '1px solid #2563eb' : '1px solid #e2e8f0',
              backgroundColor: showActiveOnly ? '#dbeafe' : '#f8fafc',
              color: showActiveOnly ? '#1d4ed8' : '#64748b',
              cursor: 'pointer',
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}
            title={showActiveOnly ? 'Showing active item only' : 'Showing all markup'}
          >
            Active Only
          </button>
        )}

        {/* Stats */}
        {equipmentCount && step === 3 && (
          <span style={{ fontSize: 12, color: 'var(--raven-text-secondary)', whiteSpace: 'nowrap' }}>
            <strong style={{ color: '#374151' }}>{equipmentCount.marked}</strong>/{equipmentCount.total}
          </span>
        )}
        {nodeCount && step === 4 && (
          <span style={{ fontSize: 12, color: 'var(--raven-text-secondary)', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
            <strong style={{ color: '#374151' }}>{nodeCount.marked}</strong>/{nodeCount.total}
            {nodeCount.skipped && nodeCount.skipped > 0 && (
              <span style={{ color: '#8b5cf6', fontSize: 8 }}>
                ({nodeCount.skipped} skipped)
              </span>
            )}
          </span>
        )}

        {/* Next button */}
        {hasNext && onNext && (
          <button
            onClick={onNext}
            style={{
              height: 22,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '0 8px',
                minHeight: 24,
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            Next →
          </button>
        )}
      </div>

      {/* Target row */}
      {showTarget && drawTarget && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: 'var(--raven-text-secondary)', fontWeight: 700, letterSpacing: '0.06em' }}>
            TARGET
          </span>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              padding: '2px 5px',
              backgroundColor: getTargetStyle(drawTarget.tone).bg,
              border: `1px solid ${getTargetStyle(drawTarget.tone).border}`,
              borderRadius: 999,
              fontSize: 12,
              maxWidth: 400,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            <span style={{ fontWeight: 600, color: '#111827' }}>{drawTarget.label}</span>
            {drawTarget.detail && (
              <span style={{ color: 'var(--raven-text-secondary)' }}>{drawTarget.detail}</span>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default ContextBar;
